import { useEffect, useId, useRef, useState } from 'react';
import { Button, cn, Icon } from '@evogard/ui';

export type PermissionCatalog = Array<{
  resource: string;
  label: string;
  actions: Array<{ action: string; label: string }>;
}>;

/**
 * O nome de cada módulo, pelo prefixo do recurso (`billing.desk` → Cobrança).
 *
 * O catálogo do servidor não carrega o módulo: ele é a junção dos catálogos de
 * cada módulo (`apps/api/src/modules/catalog.ts`), e o nome do recurso começa
 * pelo do módulo por convenção. Prefixo que não está aqui NÃO some — aparece
 * com o próprio prefixo como nome, e um módulo novo continua concedível no dia
 * em que declarar a primeira permissão, só com o nome cru até alguém escrevê-lo
 * aqui.
 *
 * Os nomes são os do menu (`menu.ts`), onde existem — a pessoa reconhece o
 * módulo pelo nome que vê no rail.
 */
const MODULE_LABELS: Record<string, string> = {
  platform: 'Plataforma',
  tickets: 'Ticket',
  portal: 'Portal do associado',
  erp: 'ERP',
  retention: 'Retenção',
  billing: 'Cobrança',
  sales: 'Comercial',
  claims: 'Sinistros',
  finance: 'Financeiro',
  management: 'Gestão e premiação',
};

type Resource = PermissionCatalog[number];

interface Module {
  id: string;
  label: string;
  resources: Resource[];
  keys: string[];
}

const keysOf = (r: Resource) => r.actions.map((a) => `${r.resource}:${a.action}`);

/**
 * Agrupa o catálogo por módulo, na ordem em que o servidor o compõe — a mesma
 * de `catalog.ts`. Nada reordena: a ordem é do catálogo, não desta tela.
 */
function byModule(catalog: PermissionCatalog): Module[] {
  const modules = new Map<string, Module>();
  for (const r of catalog) {
    const id = r.resource.split('.')[0]!;
    let m = modules.get(id);
    if (!m) {
      m = { id, label: MODULE_LABELS[id] ?? id, resources: [], keys: [] };
      modules.set(id, m);
    }
    m.resources.push(r);
    m.keys.push(...keysOf(r));
  }
  return [...modules.values()];
}

/**
 * As permissões do grupo, por módulo: módulo → recurso → ação, desde
 * 01/10/2026.
 *
 * ## Por que por módulo, e não por recurso
 *
 * Eram `fieldset`s por recurso, com as caixas lado a lado em `flex-wrap` — e o
 * catálogo tem cerca de cinquenta recursos em dez módulos. Uma linha por
 * recurso trocava a bagunça das frases quebradas por cinquenta categorias
 * soltas. A pessoa pensa em "o que este grupo faz na Cobrança", e não em
 * "Mesa", "Esteira" e "Régua" separadas: o módulo é a linha, com a contagem e
 * a caixa que marca o módulo inteiro, e os recursos viram subtítulos dentro
 * dele.
 *
 * O rótulo de uma ação é uma FRASE, então cada ação tem a linha dela, a caixa
 * sempre na mesma coluna, e a linha inteira é o alvo do clique.
 *
 * ## Quem começa aberto
 *
 * O módulo que já concede alguma coisa — quem edita precisa ver o que o grupo
 * TEM. O resto começa fechado, e a contagem diz que está vazio sem abrir.
 *
 * ## O curinga continua fora
 *
 * "Marcar todas" e a caixa do módulo marcam as ações do catálogo, uma a uma —
 * nunca `*:*` nem `recurso:*`. O motivo é o do cabeçalho de `GroupEditor`: o
 * curinga vale também para o que ainda não existe, e não é gesto de caixa.
 */
export function PermissionPicker({
  catalog,
  marked,
  onChange,
  disabled,
}: {
  catalog: PermissionCatalog;
  marked: Set<string>;
  onChange: (next: Set<string>) => void;
  disabled?: boolean;
}) {
  const modules = byModule(catalog);
  const all = modules.flatMap((m) => m.keys);
  const markedCount = all.filter((k) => marked.has(k)).length;

  const [open, setOpen] = useState<Set<string>>(
    () =>
      new Set(modules.filter((m) => m.keys.some((k) => marked.has(k))).map((m) => m.id)),
  );
  const allOpen = open.size === modules.length;

  /** Marca ou desmarca um conjunto de chaves, preservando o resto (e o curinga). */
  const setMany = (keys: string[], on: boolean) => {
    const next = new Set(marked);
    for (const k of keys) {
      if (on) next.add(k);
      else next.delete(k);
    }
    onChange(next);
  };

  const toggleOpen = (id: string) =>
    setOpen((before) => {
      const next = new Set(before);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="overflow-hidden rounded-borda border border-borda">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-borda bg-superficie-suave px-4 py-2">
        <span className="rotulo text-texto-suave">Módulo / Permissão</span>
        <span className="flex items-center gap-3">
          <Button
            type="button"
            variant="discreto"
            size="sm"
            onClick={() =>
              setOpen(allOpen ? new Set() : new Set(modules.map((m) => m.id)))
            }
          >
            {allOpen ? 'Recolher todos' : 'Expandir todos'}
          </Button>
          <label className="flex items-center gap-2 text-xs text-texto-suave">
            <span className="tabular-nums">
              {markedCount} de {all.length}
            </span>
            <TriCheckbox
              label="Marcar todas as permissões"
              state={triState(markedCount, all.length)}
              disabled={disabled}
              onChange={(on) => setMany(all, on)}
            />
          </label>
        </span>
      </div>

      <ul>
        {modules.map((m) => (
          <ModuleRow
            key={m.id}
            module={m}
            marked={marked}
            expanded={open.has(m.id)}
            disabled={disabled}
            onToggleOpen={() => toggleOpen(m.id)}
            onSetMany={setMany}
          />
        ))}
      </ul>
    </div>
  );
}

function ModuleRow({
  module,
  marked,
  expanded,
  disabled,
  onToggleOpen,
  onSetMany,
}: {
  module: Module;
  marked: Set<string>;
  expanded: boolean;
  disabled?: boolean;
  onToggleOpen: () => void;
  onSetMany: (keys: string[], on: boolean) => void;
}) {
  const listId = useId();
  const count = module.keys.filter((k) => marked.has(k)).length;

  return (
    <li className="border-b border-borda-suave last:border-b-0">
      <div className="flex items-center gap-2 py-1.5 pl-2 pr-4">
        <Button
          type="button"
          variant="discreto"
          size="sm"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={onToggleOpen}
          className="min-w-0 flex-1 justify-start gap-2 text-left"
        >
          <Icon
            nome="seta-baixo"
            size={16}
            className={cn(
              'shrink-0 text-texto-suave transition-transform',
              !expanded && '-rotate-90',
            )}
          />
          <span className="truncate text-sm font-medium text-texto">{module.label}</span>
          <span
            className={cn(
              'shrink-0 text-xs tabular-nums',
              count > 0 ? 'text-texto' : 'text-texto-suave',
            )}
          >
            {count} de {module.keys.length}
          </span>
        </Button>
        <TriCheckbox
          label={`Todas as permissões de ${module.label}`}
          state={triState(count, module.keys.length)}
          disabled={disabled}
          onChange={(on) => onSetMany(module.keys, on)}
        />
      </div>

      {expanded && (
        <div id={listId} className="border-t border-borda-suave">
          {module.resources.map((r) => (
            <section key={r.resource} aria-label={r.label}>
              {/*
               * O recurso é subtítulo, e não outra linha que abre: dois níveis de
               * seta para chegar a uma caixa é o que deixava a lista dividida
               * demais. Ele é o mesmo rótulo que a tela de grupos mostra.
               */}
              <h4 className="rotulo border-b border-borda-suave bg-superficie-suave px-4 py-1.5 pl-10 text-texto-suave">
                {r.label}
              </h4>
              <ul>
                {r.actions.map((a) => {
                  const key = `${r.resource}:${a.action}`;
                  return (
                    <li key={key} className="border-b border-borda-suave last:border-b-0">
                      {/* a linha inteira é o alvo: a frase é longa, e a caixa sozinha é um alvo de 16px */}
                      <label className="flex min-h-10 cursor-pointer items-center justify-between gap-4 py-2 pl-10 pr-4 text-sm hover:bg-superficie-suave pointer-coarse:min-h-11">
                        <span className="min-w-0 break-words text-texto">{a.label}</span>
                        <input
                          type="checkbox"
                          checked={marked.has(key)}
                          disabled={disabled}
                          onChange={(e) => onSetMany([key], e.target.checked)}
                          className="size-4 shrink-0 rounded border-borda-forte accent-primaria-texto"
                        />
                      </label>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </li>
  );
}

type Tri = 'nenhuma' | 'parte' | 'todas';

function triState(count: number, total: number): Tri {
  if (count === 0) return 'nenhuma';
  return count === total ? 'todas' : 'parte';
}

/**
 * A caixa de três estados — o módulo (ou o catálogo) inteiro.
 *
 * `indeterminate` não é atributo de HTML, só propriedade do DOM, e por isso o
 * `ref`. Clicar no estado intermediário MARCA tudo: é o que a pessoa que vê
 * "2 de 4" e clica quer — completar —, e é como o navegador trata a caixa
 * indeterminada.
 */
function TriCheckbox({
  label,
  state,
  disabled,
  onChange,
}: {
  label: string;
  state: Tri;
  disabled?: boolean;
  onChange: (on: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'parte';
  }, [state]);

  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={state === 'todas'}
      disabled={disabled}
      onChange={() => onChange(state !== 'todas')}
      className="size-4 shrink-0 rounded border-borda-forte accent-primaria-texto"
    />
  );
}
